import React, { useState } from 'react';
import { Section, Button, FadeIn, SEO } from '../components/UI';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

export default function Contact() {
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <div className="pt-20">
      <SEO 
        title="Kontakt" 
        description="Har du spørgsmål eller ønsker du at aftale en samtale? Kontakt mig her for en uforpligtende snak."
      />
      
      {/* Hero Section */}
      <Section className="bg-transparent pt-8 md:pt-12 pb-12 md:pb-24 relative overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center relative z-10">
          <div className="lg:col-span-5">
            <FadeIn className="pt-12 md:pt-0">
              <span className="text-sorg-earth font-medium tracking-[0.2em] uppercase text-xs mb-6 block">Ræk ud</span>
              <h1 className="text-4xl md:text-7xl font-serif text-sorg-dark mb-8 leading-[1.1]">
                Kontakt
              </h1>
              <p className="text-xl text-sorg-dark/70 leading-relaxed mb-10">
                Du er altid velkommen til at kontakte mig for en uforpligtende samtale eller for at høre mere om, hvordan jeg kan støtte dig. Jeg glæder mig til at høre fra dig.
              </p>
              <div className="space-y-6">
                <div className="flex items-center space-x-4 text-sorg-dark/70">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-sorg-earth soft-shadow">
                    <Mail size={20} strokeWidth={1} />
                  </div>
                  <a href="mailto:asha@sorgskab.dk" className="hover:text-sorg-earth transition-colors">asha@sorgskab.dk</a>
                </div>
                <div className="flex items-center space-x-4 text-sorg-dark/70">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-sorg-earth soft-shadow">
                    <Phone size={20} strokeWidth={1} />
                  </div>
                  <a href="tel:+4541623552" className="hover:text-sorg-earth transition-colors">+45 41 62 35 52</a>
                </div>
                <div className="flex items-center space-x-4 text-sorg-dark/70">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-sorg-earth soft-shadow">
                    <MapPin size={20} strokeWidth={1} />
                  </div>
                  <span>København, Vesterbro</span>
                </div>
              </div>
            </FadeIn>
          </div>
          
          <div className="lg:col-span-7">
            <FadeIn direction="left" delay={0.2}>
              <div className="relative">
                <div className="absolute -inset-4 bg-sorg-beige/20 rounded-[2rem] blur-2xl -z-10 transform rotate-2"></div>
                <div className="aspect-[4/3] md:aspect-[16/10] w-full overflow-hidden rounded-[2rem] soft-shadow-lg border border-white/50">
                  <img 
                    src="https://assets.auxodev.dk/assets/88d34a44-1e08-49cc-ba2b-c330133a247e/7be4301c-d16c-4891-a75a-66f603c5bf12.jpg" 
                    alt="Kontakt - Hyggeligt skrivebord" 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                  />
                </div>
                <div className="absolute -bottom-6 md:left-0 lg:-left-6 bg-white/80 backdrop-blur-md p-6 rounded-2xl soft-shadow border border-white/50 max-w-[200px] hidden md:block">
                  <p className="text-sm font-serif text-sorg-dark italic">"Jeg svarer altid så hurtigt som muligt, typisk inden for 24 timer."</p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* Form Section */}
      <Section glass>
        <div className="max-w-3xl mx-auto relative z-10">
          <FadeIn className="text-center mb-12">
            <h2 className="text-3xl font-serif text-sorg-dark mb-4">Send en besked</h2>
            <p className="text-sorg-dark/60">Udfyld formularen herunder, så vender jeg tilbage til dig snarest.</p>
          </FadeIn>

          <FadeIn delay={0.2}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot */}
              <div className="sr-only absolute -z-10">
                <label htmlFor="website_url">Website URL</label>
                <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-sorg-dark/70 mb-2">Navn</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    required 
                    className="w-full px-6 py-4 rounded-2xl bg-white/50 border border-white/60 focus:border-sorg-earth focus:ring-1 focus:ring-sorg-earth outline-none transition-all"
                    placeholder="Dit fulde navn"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-sorg-dark/70 mb-2">E-mailadresse</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    required 
                    className="w-full px-6 py-4 rounded-2xl bg-white/50 border border-white/60 focus:border-sorg-earth focus:ring-1 focus:ring-sorg-earth outline-none transition-all"
                    placeholder="din@email.dk"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-sorg-dark/70 mb-2">Emne</label>
                <select 
                  id="subject" 
                  name="subject" 
                  className="w-full px-6 py-4 rounded-2xl bg-white/50 border border-white/60 focus:border-sorg-earth focus:ring-1 focus:ring-sorg-earth outline-none transition-all appearance-none"
                >
                  <option value="sorgvejledning">Sorgvejledning / Sorgsamtaler</option>
                  <option value="dodsdoula">Dødsdoula</option>
                  <option value="foredrag">Foredrag & Oplæg (f.eks. vågetjeneste eller forening)</option>
                  <option value="workshop-skole">Workshop for skoler / unge</option>
                  <option value="livets-afslutning">Livets afslutning</option>
                  <option value="andet">Andet</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-sorg-dark/70 mb-2">Besked</label>
                <textarea 
                  id="message" 
                  name="message" 
                  required 
                  rows="5" 
                  className="w-full px-6 py-4 rounded-2xl bg-white/50 border border-white/60 focus:border-sorg-earth focus:ring-1 focus:ring-sorg-earth outline-none transition-all resize-none"
                  placeholder="Hvordan kan jeg hjælpe dig?"
                ></textarea>
              </div>

              <div className="text-center">
                <button 
                  type="submit" 
                  className="inline-flex items-center justify-center px-8 py-3 md:px-10 md:py-4 rounded-full bg-sorg-earth text-white font-medium hover:bg-sorg-dark transition-all duration-300 soft-shadow group"
                >
                  <span>Send besked</span>
                  <Send size={18} strokeWidth={1} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              {status === 'success' && (
                <FadeIn className="p-4 bg-emerald-50 text-emerald-700 rounded-2xl text-center border border-emerald-100">
                  Tak for din besked! Jeg vender tilbage til dig hurtigst muligt.
                </FadeIn>
              )}
              {status === 'error' && (
                <FadeIn className="p-4 bg-rose-50 text-rose-700 rounded-2xl text-center border border-rose-100">
                  Der opstod en fejl. Prøv venligst igen senere eller kontakt mig direkte via email.
                </FadeIn>
              )}
            </form>
          </FadeIn>
        </div>
      </Section>
    </div>
  );
}
